import { useEffect, useState, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, SettingsShell } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, EyeOff, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { toast } from "sonner";

function Field({ label, children, hint }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function KeyInput({ value, onChange, testid, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value || ""} onChange={onChange} className="font-mono text-sm pr-10" placeholder={placeholder} data-testid={testid} />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function StatusPill({ ok, okText, badText }) {
  return ok
    ? <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" />{okText}</span>
    : <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full"><AlertCircle className="w-3.5 h-3.5" />{badText}</span>;
}

function useAll() {
  const [all, setAll] = useState(null);
  const refresh = () => { api.get("/admin/settings/all").then(({ data }) => setAll(data)).catch((e) => toast.error(formatError(e))); };
  useEffect(() => { refresh(); }, []);
  return [all, setAll, refresh];
}

async function saveKey(key, body) {
  await api.put(`/admin/settings/key/${key}`, body);
}

// ---------- SMS / Phone OTP Provider Settings ----------
const SMS_PROVIDERS = ["Disabled", "Firebase", "MSG91", "Fast2SMS"];

const PROVIDER_META = {
  Firebase: {
    color: "text-orange-600",
    bg: "bg-orange-50 border-orange-200",
    activeBg: "bg-orange-600/10 border-orange-500",
    badge: "bg-orange-100 text-orange-700",
    desc: "Google Firebase Authentication — Phone OTP via Firebase project. Requires Web API Key, Project ID, and App ID from the Firebase console.",
    docsUrl: "https://console.firebase.google.com/",
    docsLabel: "Firebase Console →",
    icon: "🔥",
  },
  MSG91: {
    color: "text-blue-600",
    bg: "bg-blue-50 border-blue-200",
    activeBg: "bg-blue-600/10 border-blue-500",
    badge: "bg-blue-100 text-blue-700",
    desc: "MSG91 — Popular Indian SMS gateway. Create an OTP Widget in your MSG91 dashboard and paste the Widget ID here.",
    docsUrl: "https://msg91.com/",
    docsLabel: "MSG91 Dashboard →",
    icon: "💬",
  },
  Fast2SMS: {
    color: "text-green-600",
    bg: "bg-green-50 border-green-200",
    activeBg: "bg-green-600/10 border-green-500",
    badge: "bg-green-100 text-green-700",
    desc: "Fast2SMS — Affordable Indian bulk SMS & OTP gateway. Paste your API Authorization Key from the Fast2SMS developer panel.",
    docsUrl: "https://www.fast2sms.com/dashboard",
    docsLabel: "Fast2SMS Dashboard →",
    icon: "⚡",
  },
};

const EMPTY_SETTINGS = {
  enabled: false,
  provider: "Disabled",
  fast2sms_api_key: "",
  msg91_api_key: "",
  msg91_template_id: "",
  msg91_flow_id: "",
  msg91_sender_id: "",
  firebase_web_api_key: "",
  firebase_project_id: "",
  firebase_app_id: "",
};

export function SettingsSMS() {
  const [s, setS] = useState(EMPTY_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then(({ data }) => {
        const pl = data.phone_login || {};
        setS({ ...EMPTY_SETTINGS, ...pl });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const upd = (k, v) => setS((d) => ({ ...d, [k]: v }));

  const selectProvider = (p) => {
    setS((d) => ({
      ...d,
      provider: p,
      enabled: p !== "Disabled" ? d.enabled : false,
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS({ ...EMPTY_SETTINGS, ...(data.phone_login || {}) });
      toast.success("SMS / Phone OTP settings saved!");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <Loading />;

  const activeProvider = s.provider;

  return (
    <div>
      <PageHeader title="SMS / Phone OTP" subtitle="Configure phone number login & OTP delivery provider" />

      {/* Master enable toggle */}
      <div className="mb-6 flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30">
        <div>
          <p className="font-semibold text-sm">Enable Phone OTP Login</p>
          <p className="text-xs text-muted-foreground mt-0.5">Allow users to register & sign in using mobile number + OTP</p>
        </div>
        <Switch
          checked={s.enabled}
          onCheckedChange={(v) => {
            upd("enabled", v);
            if (v && s.provider === "Disabled") upd("provider", "Fast2SMS");
          }}
          data-testid="phone-login-toggle"
        />
      </div>

      {/* Provider selector cards */}
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Choose Active Provider</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {SMS_PROVIDERS.map((p) => {
            const meta = PROVIDER_META[p];
            const isActive = activeProvider === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => selectProvider(p)}
                data-testid={`provider-${p.toLowerCase()}`}
                className={`relative flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-sm font-medium transition-all
                  ${isActive
                    ? (meta ? meta.activeBg : "border-[#6B1A24] bg-[#6B1A24]/10 text-[#6B1A24]")
                    : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-600"
                  }`}
              >
                {meta && <span className="text-2xl">{meta.icon}</span>}
                {!meta && <span className="text-2xl">🚫</span>}
                <span>{p}</span>
                {isActive && (
                  <span className="absolute top-2 right-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Per-provider config panels */}
      {activeProvider === "Fast2SMS" && (
        <ProviderPanel
          meta={PROVIDER_META.Fast2SMS}
          name="Fast2SMS"
          hint="Get your API Authorization Key from Fast2SMS Developer > Dev API."
        >
          <Field label="API Authorization Key" hint="Paste the key from Fast2SMS Dev API page.">
            <KeyInput
              value={s.fast2sms_api_key}
              onChange={(e) => upd("fast2sms_api_key", e.target.value)}
              testid="fast2sms-api-key"
              placeholder="Your Fast2SMS authorization key"
            />
          </Field>
        </ProviderPanel>
      )}

      {activeProvider === "MSG91" && (
        <ProviderPanel
          meta={PROVIDER_META.MSG91}
          name="MSG91"
          hint="Create and configure an OTP Widget in your MSG91 dashboard, then copy the details below."
        >
          <Field label="Auth Key" hint="Found in MSG91 Dashboard > API > Auth Key">
            <KeyInput
              value={s.msg91_api_key}
              onChange={(e) => upd("msg91_api_key", e.target.value)}
              testid="msg91-api-key"
              placeholder="MSG91 Auth Key"
            />
          </Field>
          <Field label="Widget ID (OTP)" hint="Your 24-character MSG91 OTP Widget ID (from MSG91 Dashboard > OTP > Widget)">
            <Input
              value={s.msg91_template_id}
              onChange={(e) => upd("msg91_template_id", e.target.value)}
              className="font-mono text-sm"
              data-testid="msg91-template-id"
              placeholder="e.g. 366679653236343330363038"
            />
          </Field>
          <Field label="Flow ID (Transactional SMS)" hint="Your MSG91 Flow ID for sending transactional notification messages (e.g. chat fallback)">
            <Input
              value={s.msg91_flow_id}
              onChange={(e) => upd("msg91_flow_id", e.target.value)}
              className="font-mono text-sm"
              data-testid="msg91-flow-id"
              placeholder="e.g. 6a3cc8ea3a2e24b2790cda03"
            />
          </Field>
          <Field label="Sender ID" hint="Your registered 6-character sender ID header (e.g. HMRSHT)">
            <Input
              value={s.msg91_sender_id}
              onChange={(e) => upd("msg91_sender_id", e.target.value)}
              className="font-mono text-sm uppercase"
              maxLength={6}
              data-testid="msg91-sender-id"
              placeholder="e.g. HMRSHT"
            />
          </Field>
        </ProviderPanel>
      )}

      {activeProvider === "Firebase" && (
        <ProviderPanel
          meta={PROVIDER_META.Firebase}
          name="Firebase"
          hint="Phone Authentication via Firebase. Get these values from your Firebase console > Project Settings > Web App."
        >
          <Field label="Web API Key" hint="Firebase Console > Project Settings > General > Web API Key">
            <KeyInput
              value={s.firebase_web_api_key}
              onChange={(e) => upd("firebase_web_api_key", e.target.value)}
              testid="firebase-web-api-key"
              placeholder="AIza..."
            />
          </Field>
          <Field label="Project ID" hint="Firebase Console > Project Settings > Project ID">
            <Input
              value={s.firebase_project_id}
              onChange={(e) => upd("firebase_project_id", e.target.value)}
              className="font-mono text-sm"
              data-testid="firebase-project-id"
              placeholder="my-firebase-project"
            />
          </Field>
          <Field label="App ID" hint="Firebase Console > Project Settings > Your apps > App ID">
            <Input
              value={s.firebase_app_id}
              onChange={(e) => upd("firebase_app_id", e.target.value)}
              className="font-mono text-sm"
              data-testid="firebase-app-id"
              placeholder="1:1234567890:web:abcdef..."
            />
          </Field>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
            ⚠️ Firebase Spark (free) plan does NOT support Phone Auth. You need the <strong>Blaze (pay-as-you-go)</strong> plan for SMS OTP.
          </div>
        </ProviderPanel>
      )}

      {activeProvider === "Disabled" && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-slate-400 text-sm">
          <p className="text-3xl mb-2">🚫</p>
          <p className="font-medium">Phone OTP is disabled</p>
          <p className="text-xs mt-1">Select a provider above to configure SMS delivery.</p>
        </div>
      )}

      <div className="flex justify-end mt-6">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#6B1A24] hover:bg-[#52141C] px-8"
          data-testid="settings-sms-save-btn"
        >
          {saving ? "Saving…" : "Save SMS settings"}
        </Button>
      </div>
    </div>
  );
}

function ProviderPanel({ meta, name, hint, children }) {
  return (
    <div className={`rounded-xl border-2 p-5 mb-4 space-y-4 ${meta.activeBg}`}>
      <div className="flex items-start gap-3">
        <span className="text-2xl">{meta.icon}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`font-bold text-sm ${meta.color}`}>{name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.badge}`}>Active Provider</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
          <a href={meta.docsUrl} target="_blank" rel="noopener noreferrer" className={`text-xs font-medium ${meta.color} hover:underline`}>
            {meta.docsLabel}
          </a>
        </div>
      </div>
      <div className="space-y-4 border-t border-current/10 pt-4">
        {children}
      </div>
    </div>
  );
}



// ---------- Payments ----------
export function SettingsPayments() {
  const [all, , refresh] = useAll();
  const [pay, setPay] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (all) setPay(all.payment); }, [all]);
  if (!all || !pay) return <Loading />;
  const save = async () => { 
    setSaving(true); 
    try { 
      await saveKey("payment", pay); 
      toast.success("Payment settings saved"); 
      if (refresh) refresh();
    } catch (e) { 
      toast.error(formatError(e)); 
    } finally { 
      setSaving(false); 
    } 
  };
  return (
    <div>
      <PageHeader title="Payment Gateway" subtitle="Razorpay configuration" />
      <SettingsShell title="Razorpay" desc="Configure Razorpay API keys. Stored keys take precedence over server environment keys. Manage all subscription plans using the Package Builder under the Membership menu.">
        <Field label="Gateway status">
          <StatusPill ok={all.payment_keys.razorpay_configured} okText="Razorpay configured" badText="Keys missing in environment" />
        </Field>
        <Field label="Key ID">
          <Input 
            value={pay.key_id || ""} 
            onChange={(e) => setPay((d) => ({ ...d, key_id: e.target.value }))} 
            className="font-mono text-sm" 
            placeholder={all.payment_keys.key_id || "rzp_live_..."}
            data-testid="razorpay-keyid" 
          />
        </Field>
        <Field label="Key Secret">
          <KeyInput 
            value={pay.key_secret || ""} 
            onChange={(e) => setPay((d) => ({ ...d, key_secret: e.target.value }))} 
            placeholder="Razorpay Key Secret"
            testid="razorpay-keysecret" 
          />
        </Field>
        <Field label="Webhook Secret (Optional)">
          <KeyInput 
            value={pay.webhook_secret || ""} 
            onChange={(e) => setPay((d) => ({ ...d, webhook_secret: e.target.value }))} 
            placeholder="Razorpay Webhook Secret"
            testid="razorpay-webhooksecret" 
          />
        </Field>
        <div className="flex items-center justify-between">
          <Label className="text-sm">Enable Razorpay checkout</Label>
          <Switch checked={pay.razorpay_enabled} onCheckedChange={(v) => setPay((d) => ({ ...d, razorpay_enabled: v }))} data-testid="razorpay-toggle" />
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#6B1A24] hover:bg-[#52141C] self-start" data-testid="settings-payments-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
      </SettingsShell>
    </div>
  );
}

// ---------- Email ----------
export function SettingsEmail() {
  const [all] = useAll();
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const fetchLogs = useCallback(async (p = 1) => {
    setLoadingLogs(true);
    try {
      const { data } = await api.get(`/admin/logs/email?limit=15&page=${p}`);
      setLogs(data.logs);
      setTotal(data.total);
      setPage(p);
    } catch (e) {
      console.error("Error loading email logs:", e);
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  if (!all) return <Loading />;

  const test = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/test", { to: to || undefined });
      if (data.ok) {
        toast.success(`Test email sent to ${data.to}`);
        fetchLogs(1);
      }
      else toast.error(data.error ? `Email failed: ${data.error}` : "Email failed — check SMTP config");
    }
    catch (e) { toast.error(formatError(e)); } finally { setSending(false); }
  };

  return (
    <div>
      <PageHeader title="Email / SMTP" subtitle="Transactional email delivery" />
      <SettingsShell title="SMTP delivery" desc="SMTP credentials are configured in the server environment (Hostinger). Send a test email to verify delivery.">
        <Field label="Status"><StatusPill ok={all.email_smtp.configured} okText="SMTP configured" badText="SMTP not configured" /></Field>
        <Field label="SMTP host"><Input value={all.email_smtp.host} readOnly className="bg-slate-50" data-testid="smtp-host" /></Field>
        <Field label="From address"><Input value={all.email_smtp.from} readOnly className="bg-slate-50" data-testid="smtp-from" /></Field>
        <Field label="Send a test email" hint="Leave blank to send to your own admin email.">
          <div className="flex gap-2">
            <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" data-testid="email-test-to" />
            <Button onClick={test} disabled={sending} className="bg-[#6B1A24] hover:bg-[#52141C] shrink-0" data-testid="email-test-btn"><Send className="w-4 h-4 mr-1.5" />{sending ? "Sending…" : "Send test"}</Button>
          </div>
        </Field>
      </SettingsShell>

      <div className="mt-8 border border-border rounded-xl p-6 bg-card">
        <h3 className="font-semibold text-base text-[#2A0A0E] mb-1">Email Delivery History</h3>
        <p className="text-xs text-muted-foreground mb-4">View log of transactional emails sent to members.</p>
        
        {loadingLogs ? (
          <div className="text-center py-6 text-sm text-muted-foreground">Loading history...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">No email logs found.</div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto border border-slate-100 rounded-lg">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-100">
                    <th className="p-3">To Email</th>
                    <th className="p-3">Subject</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Error</th>
                    <th className="p-3">Date & Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50/50">
                      <td className="p-3 font-medium text-slate-700">{log.to_email}</td>
                      <td className="p-3 text-slate-600">{log.subject}</td>
                      <td className="p-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          log.status === "success" 
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100" 
                            : "bg-red-50 text-red-700 border border-red-100"
                        }`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="p-3 text-red-500 font-mono truncate max-w-[200px]" title={log.error}>{log.error || "-"}</td>
                      <td className="p-3 text-slate-400">
                        {log.created_at ? new Date(log.created_at).toLocaleString("en-IN") : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">Total: {total} records</span>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  disabled={page <= 1} 
                  onClick={() => fetchLogs(page - 1)}
                  className="h-8 px-3 rounded-lg"
                >
                  Previous
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  disabled={page * 15 >= total} 
                  onClick={() => fetchLogs(page + 1)}
                  className="h-8 px-3 rounded-lg"
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Captcha ----------
export function SettingsCaptcha() {
  const [all] = useAll();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (all) setC(all.captcha); }, [all]);
  if (!all || !c) return <Loading />;
  const save = async () => { setSaving(true); try { await saveKey("captcha", c); toast.success("Captcha settings saved"); } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); } };
  return (
    <div>
      <PageHeader title="Captcha" subtitle="Bot protection on login & signup" />
      <SettingsShell title="Captcha provider" desc="Protect signup and login forms from bots. Add your provider's site & secret keys, then enable. Keys are stored securely.">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Enable captcha</Label>
          <Switch checked={c.enabled} onCheckedChange={(v) => setC((d) => ({ ...d, enabled: v }))} data-testid="captcha-toggle" />
        </div>
        <Field label="Provider">
          <Select value={c.provider} onValueChange={(v) => setC((d) => ({ ...d, provider: v }))}>
            <SelectTrigger data-testid="captcha-provider"><SelectValue /></SelectTrigger>
            <SelectContent>{["reCAPTCHA v2", "reCAPTCHA v3", "hCaptcha", "Cloudflare Turnstile"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Site key"><KeyInput value={c.site_key} onChange={(e) => setC((d) => ({ ...d, site_key: e.target.value }))} testid="captcha-site-key" /></Field>
        <Field label="Secret key"><KeyInput value={c.secret_key} onChange={(e) => setC((d) => ({ ...d, secret_key: e.target.value }))} testid="captcha-secret-key" /></Field>
        <Button onClick={save} disabled={saving} className="bg-[#6B1A24] hover:bg-[#52141C] self-start" data-testid="settings-captcha-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
      </SettingsShell>
    </div>
  );
}

// ---------- Push ----------
export function SettingsPush() {
  const [all] = useAll();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Broadcast state - must be BEFORE any early return
  const [channel, setChannel] = useState("push");
  const [target, setTarget] = useState("all");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  const fetchLogs = useCallback(async (p = 1) => {
    setLoadingLogs(true);
    try {
      const { data } = await api.get(`/admin/logs/push?limit=15&page=${p}`);
      setLogs(data.logs);
      setTotal(data.total);
      setPage(p);
    } catch (e) {
      console.error("Error loading push logs:", e);
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    if (all) {
      setP(all.push);
      fetchLogs(1);
    }
  }, [all, fetchLogs]);

  if (!all || !p) return <Loading />;

  const save = async () => {
    setSaving(true);
    try {
      await saveKey("push", p);
      toast.success("Push settings saved");
      fetchLogs(1);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSendBroadcast = async () => {
    if (!subject || !message) {
      return toast.error("Please fill in Title/Subject and Message");
    }
    setSendingBroadcast(true);
    try {
      const { data } = await api.post("/admin/broadcast", {
        channel,
        target,
        subject,
        message,
        delay_seconds: 1.0,
        batch_size: 100,
        batch_pause_seconds: 5.0
      });
      if (data.ok) {
        toast.success(data.message || `Broadcast job ${data.job_id} started successfully.`);
        setSubject("");
        setMessage("");
        fetchLogs(1);
      } else {
        toast.error(data.message || "Failed to start broadcast job.");
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSendingBroadcast(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader title="Push Notifications" subtitle="Web & mobile push delivery" />
      <SettingsShell title="Push provider" desc="Configure Firebase Cloud Messaging (or another provider) to send push notifications for new interests and messages. Add your server key and enable.">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Enable push notifications</Label>
          <Switch checked={p.enabled} onCheckedChange={(v) => setP((d) => ({ ...d, enabled: v }))} data-testid="push-toggle" />
        </div>
        <Field label="Provider">
          <Select value={p.provider} onValueChange={(v) => setP((d) => ({ ...d, provider: v }))}>
            <SelectTrigger data-testid="push-provider"><SelectValue /></SelectTrigger>
            <SelectContent>{["Firebase Cloud Messaging", "OneSignal", "Web Push (VAPID)"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Server / API key"><KeyInput value={p.server_key} onChange={(e) => setP((d) => ({ ...d, server_key: e.target.value }))} testid="push-server-key" /></Field>
        <Button onClick={save} disabled={saving} className="bg-[#6B1A24] hover:bg-[#52141C] self-start" data-testid="settings-push-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
      </SettingsShell>

      <SettingsShell title="Send Custom Broadcast" desc="Send a real-time push notification or email broadcast immediately to your registered users.">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Broadcast Channel">
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="push">Push Notification Only</SelectItem>
                <SelectItem value="email">Email Broadcast Only</SelectItem>
                <SelectItem value="both">Both (Push & Email)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Target Audience">
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Registered Users</SelectItem>
                <SelectItem value="incomplete_profile">Incomplete Profiles Only</SelectItem>
                <SelectItem value="no_push_token">Users without Push Tokens</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Notification Title / Email Subject">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. New Matches Available!" />
        </Field>
        <Field label="Message Content (Push Body)">
          <textarea 
            value={message} 
            onChange={(e) => setMessage(e.target.value)} 
            placeholder="Write your push notification message here..." 
            className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
        </Field>
        <Button 
          onClick={handleSendBroadcast} 
          disabled={sendingBroadcast} 
          className="bg-[#6B1A24] hover:bg-[#52141C] self-start"
        >
          {sendingBroadcast ? "Sending Broadcast..." : "Send Broadcast"}
        </Button>
      </SettingsShell>

      <div className="mt-8 border border-border rounded-xl p-6 bg-card">
        <h3 className="font-semibold text-base text-[#2A0A0E] mb-1">Push Notification Log</h3>
        <p className="text-xs text-muted-foreground mb-4">View log of Firebase Cloud Messaging push alerts sent to devices.</p>
        
        {loadingLogs ? (
          <div className="text-center py-6 text-sm text-muted-foreground">Loading history...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">No push logs found.</div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto border border-slate-100 rounded-lg">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-100">
                    <th className="p-3">Recipient</th>
                    <th className="p-3">Title</th>
                    <th className="p-3">Body</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Error / Response</th>
                    <th className="p-3">Date & Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50/50">
                      <td className="p-3">
                        <div className="font-medium text-slate-700">{log.user_name}</div>
                        <div className="text-[10px] text-slate-400">{log.user_email}</div>
                      </td>
                      <td className="p-3 text-slate-600 font-medium">{log.title}</td>
                      <td className="p-3 text-slate-500 max-w-[200px] truncate" title={log.body}>{log.body}</td>
                      <td className="p-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          log.status === "success" 
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100" 
                            : log.status === "skipped"
                            ? "bg-amber-50 text-amber-700 border border-amber-100"
                            : "bg-red-50 text-red-700 border border-red-100"
                        }`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="p-3 text-slate-500 font-mono truncate max-w-[150px]" title={log.error || log.response}>
                        {log.error || log.response || "-"}
                      </td>
                      <td className="p-3 text-slate-400">
                        {log.created_at ? new Date(log.created_at).toLocaleString("en-IN") : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">Total: {total} records</span>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  disabled={page <= 1} 
                  onClick={() => fetchLogs(page - 1)}
                  className="h-8 px-3 rounded-lg"
                >
                  Previous
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  disabled={page * 15 >= total} 
                  onClick={() => fetchLogs(page + 1)}
                  className="h-8 px-3 rounded-lg"
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Email Notifications ----------
const NOTIF_LABELS = {
  interest_received: "New interest received",
  interest_accepted: "Interest accepted",
  message_received: "New message received",
  profile_viewed: "Profile viewed",
  photo_requested: "Photo requested",
};
export function SettingsNotifications() {
  const [all] = useAll();
  const [n, setN] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (all) setN(all.email_notifications); }, [all]);
  if (!all || !n) return <Loading />;
  const save = async () => { setSaving(true); try { await saveKey("email_notifications", n); toast.success("Notification settings saved"); } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); } };
  return (
    <div>
      <PageHeader title="Email Notifications" subtitle="Choose which events trigger emails to users" />
      <SettingsShell title="Notification events" desc="Toggle which user events send an automated email. Disabling an event stops those emails platform-wide immediately.">
        {Object.keys(NOTIF_LABELS).map((k) => (
          <div key={k} className="flex items-center justify-between py-1">
            <Label className="text-sm">{NOTIF_LABELS[k]}</Label>
            <Switch checked={!!n[k]} onCheckedChange={(v) => setN((d) => ({ ...d, [k]: v }))} data-testid={`notif-${k}`} />
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="bg-[#6B1A24] hover:bg-[#52141C] self-start" data-testid="settings-notifications-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
      </SettingsShell>
    </div>
  );
}

// ---------- Google Social Login ----------
export function SettingsGoogle() {
  const [all] = useAll();
  const [g, setG] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (all) setG(all.google_login); }, [all]);

  if (!all || !g) return <Loading />;

  const save = async () => {
    setSaving(true);
    try {
      await saveKey("google_login", g);
      toast.success("Google login settings saved");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Google Social Login" subtitle="Google Sign-in configuration" />
      <SettingsShell title="Google Login Setup" desc="Configure client ID and client secret from Google Cloud Console to enable Google Login for standard users.">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Enable Google Sign-in</Label>
          <Switch checked={g.enabled} onCheckedChange={(v) => setG((d) => ({ ...d, enabled: v }))} data-testid="google-toggle" />
        </div>
        <Field label="Google Client ID">
          <Input value={g.client_id || ""} onChange={(e) => setG((d) => ({ ...d, client_id: e.target.value }))} placeholder="12345-abcde.apps.googleusercontent.com" className="font-mono text-sm" data-testid="google-client-id" />
        </Field>
        <Field label="Google Client Secret">
          <KeyInput value={g.client_secret || ""} onChange={(e) => setG((d) => ({ ...d, client_secret: e.target.value }))} placeholder="Client Secret Key" testid="google-client-secret" />
        </Field>
        <Field label="Authorized Callback / Redirect URL" hint="This URL must match exactly what is registered in your Google Cloud Console.">
          <Input value={g.callback_url || "https://hamarsathi.com/user/social-login/callback/google"} readOnly className="font-mono text-sm bg-slate-50" data-testid="google-callback-url" />
        </Field>
        <Button onClick={save} disabled={saving} className="bg-[#6B1A24] hover:bg-[#52141C] self-start" data-testid="settings-google-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
      </SettingsShell>
    </div>
  );
}

// ---------- Core Features Settings ----------
export function SettingsCore() {
  const [all, setAll] = useAll();
  const [core, setCore] = useState(null);
  const [google, setGoogle] = useState(null);
  const [phone, setPhone] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (all) {
      setCore(all.core_features || {});
      setGoogle(all.google_login || {});
      setPhone(all.phone_login || {});
    }
  }, [all]);

  if (!all || !core || !google || !phone) return <Loading />;

  const save = async () => {
    setSaving(true);
    try {
      await saveKey("core_features", core);
      await saveKey("google_login", google);
      await api.put("/admin/settings", {
        enabled: phone.enabled,
        provider: phone.provider === "Disabled" && phone.enabled ? "Firebase" : phone.provider
      });
      toast.success("Core features settings saved successfully");
      const { data } = await api.get("/admin/settings/all");
      setAll(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Core Features Management" subtitle="Configure and toggle the core MVP features" />
      
      <div className="space-y-8">
        <SettingsShell title="Authentication Options" desc="Enable or disable standard email, mobile OTP, and Google Social login methods globally.">
          <div className="flex items-center justify-between py-1">
            <div className="flex flex-col gap-0.5">
              <Label className="text-sm font-semibold">Email Authentication</Label>
              <span className="text-xs text-slate-400">Allow users to register and sign in using email and password</span>
            </div>
            <Switch checked={core.email_login_enabled} onCheckedChange={(v) => setCore((d) => ({ ...d, email_login_enabled: v }))} data-testid="core-email-toggle" />
          </div>

          <div className="flex items-center justify-between py-1 border-t border-slate-100 pt-4">
            <div className="flex flex-col gap-0.5">
              <Label className="text-sm font-semibold">Phone OTP Authentication</Label>
              <span className="text-xs text-slate-400">Allow users to log in instantly with their mobile number via SMS OTP</span>
            </div>
            <Switch checked={phone.enabled} onCheckedChange={(v) => setPhone((d) => ({ ...d, enabled: v }))} data-testid="core-phone-toggle" />
          </div>

          <div className="flex items-center justify-between py-1 border-t border-slate-100 pt-4">
            <div className="flex flex-col gap-0.5">
              <Label className="text-sm font-semibold">Google Social Sign-In</Label>
              <span className="text-xs text-slate-400">Allow users to continue with Google OAuth login</span>
            </div>
            <Switch checked={google.enabled} onCheckedChange={(v) => setGoogle((d) => ({ ...d, enabled: v }))} data-testid="core-google-toggle" />
          </div>
        </SettingsShell>

        <SettingsShell title="Referrals & Viral Loops" desc="Manage settings for the built-in referral code system.">
          <div className="flex items-center justify-between py-1">
            <div className="flex flex-col gap-0.5">
              <Label className="text-sm font-semibold">Enable Referral System</Label>
              <span className="text-xs text-slate-400">Generate referral codes for new users and track signups</span>
            </div>
            <Switch checked={core.referrals_enabled} onCheckedChange={(v) => setCore((d) => ({ ...d, referrals_enabled: v }))} data-testid="core-referrals-toggle" />
          </div>
        </SettingsShell>

        <SettingsShell title="WhatsApp Custom Sharing" desc="Configure the template message when users share their profile or referral link on WhatsApp.">
          <Field label="WhatsApp Message Template" hint="Use {link} to place the referral link dynamically.">
            <textarea
              value={core.whatsapp_template || ""}
              onChange={(e) => setCore((d) => ({ ...d, whatsapp_template: e.target.value }))}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-sans"
              placeholder="Find your perfect match on Hamarsathi..."
              data-testid="core-whatsapp-template"
            />
          </Field>
        </SettingsShell>

        <SettingsShell title="Profile Verification & Alerts" desc="Configure mandatory onboarding steps and automated SMS alert behaviors.">
          <div className="flex items-center justify-between py-1">
            <div className="flex flex-col gap-0.5">
              <Label className="text-sm font-semibold">Require Photo during Onboarding</Label>
              <span className="text-xs text-slate-400">Force users to upload a photo before completing onboarding</span>
            </div>
            <Switch checked={core.require_photo_onboarding} onCheckedChange={(v) => setCore((d) => ({ ...d, require_photo_onboarding: v }))} data-testid="core-photo-toggle" />
          </div>

          <div className="flex items-center justify-between py-1 border-t border-slate-100 pt-4">
            <div className="flex flex-col gap-0.5">
              <Label className="text-sm font-semibold">SMS Alerts for Express Interest</Label>
              <span className="text-xs text-slate-400">Send an SMS notification immediately when interest is received or accepted</span>
            </div>
            <Switch checked={core.interest_sms_enabled} onCheckedChange={(v) => setCore((d) => ({ ...d, interest_sms_enabled: v }))} data-testid="core-interest-sms-toggle" />
          </div>
        </SettingsShell>

        <div className="flex justify-end pt-4">
          <Button onClick={save} disabled={saving} className="bg-[#6B1A24] hover:bg-[#52141C] px-8" data-testid="settings-core-save-btn">
            {saving ? "Saving changes..." : "Save all core settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Loading() { return <div className="text-slate-400 py-12">Loading…</div>; }
